import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'tea-component';
import { useLocation } from 'umi';
import './index.scss';
import { HOURLYUPDATEPAGE, POE2 } from '@op/constants/specialGame';
import { influencerCommonTipsText, TipsMapType } from '../BubbleCommon';
import { LanguageType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import UnloadModal from '@home/pages/home/Explore/components/UnloadModal';
import useLanguage from '@op/hooks/useLanguage';
import { CONTACT_EMAIL } from '@op/constants';

const TimeZeroTips = (value: number) => {
  return {
    en: (
      <>
        The Data for this game is set to <b>UTC {value}</b> timezone
      </>
    ),
    zh: (
      <>
        该游戏特性处理为 <b>UTC {value}</b> 时区
      </>
    ),
  };
};

const HourlyUpdateTips = {
  en: 'The data from each channel is currently updated hourly.',
  zh: '各渠道数据当前为小时级更新。',
};

export default function TimezoneTips({
  wholeGameInfo,
  hasMargin = false,
  horseRaceLampTips,
  isInfluencer = false,
  noShowTimeZoneTips = false,
}: {
  wholeGameInfo: any;
  hasMargin?: boolean;
  horseRaceLampTips?: TipsMapType | Array<TipsMapType>;
  isInfluencer?: boolean;
  noShowTimeZoneTips?: boolean;
}) {
  const location = useLocation();
  const [closeTips, setCloseTips] = useState(false);
  const { isEn } = useLanguage();
  const key: LanguageType = isEn ? 'en' : 'zh';
  const { showTips, switchFeedbackWindows, stopSourceTips, open } =
    useStopSource({
      wholeGameInfo,
    });

  const specialAlert = useMemo(() => {
    const timezone = DBO_TM.timezone();
    const isTimeZero = timezone !== 8;

    const isHourlyUpdatePage = HOURLYUPDATEPAGE.includes(location.pathname);
    const isPOE2 = POE2.includes(wholeGameInfo?.edition_unified_id);

    const list: { show: boolean; tips?: TipsMapType }[] = [
      { show: isTimeZero && !noShowTimeZoneTips, tips: TimeZeroTips(timezone) },
      { show: isHourlyUpdatePage && isPOE2, tips: HourlyUpdateTips },
      {
        show: !wholeGameInfo?.is_expanded && isInfluencer,
        tips: influencerCommonTipsText,
      },
      {
        show: showTips,
        tips: stopSourceTips,
      },
    ];

    if (horseRaceLampTips) {
      if (Array.isArray(horseRaceLampTips)) {
        horseRaceLampTips.forEach((item) => {
          list.push({ show: true, tips: item });
        });
      } else {
        list.push({ show: true, tips: horseRaceLampTips });
      }
    }

    return list
      .map((item) => {
        if (item.show) return item.tips;
      })
      .filter(Boolean);
  }, [wholeGameInfo, horseRaceLampTips, showTips, noShowTimeZoneTips]);

  return (
    <>
      <Alert
        className={`timezone-tips-wrapper ${hasMargin ? 'has-margin' : ''}`}
        visible={!closeTips && specialAlert.length > 0}
        hideIcon
        onClose={() => setCloseTips(true)}
      >
        <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
          {specialAlert.length > 0 &&
            specialAlert.map((item: any, index) => {
              return (
                <li key={index}>
                  <span>{item?.[key]}</span>
                </li>
              );
            })}
        </ul>
      </Alert>
      <UnloadModal visible={open} close={switchFeedbackWindows} />
    </>
  );
}

function useStopSource({ wholeGameInfo }: { wholeGameInfo: any }) {
  const [showTips, setShowTips] = useState(false);
  useEffect(() => {
    setShowTips(wholeGameInfo?.show_warn);
  }, [wholeGameInfo]);

  const [open, setOpen] = useState(false);
  const switchFeedbackWindows = () => {
    setOpen((value) => !value);
  };

  return {
    showTips,
    switchFeedbackWindows,
    open,
    stopSourceTips: {
      en: (
        <>
          The game《{wholeGameInfo.entity_name}》has not been visited for a
          while, so data updates have been stopped to save resources. If you
          need to view the latest data, please contact {CONTACT_EMAIL} or submit
          your requirements in the &quot;
          <Button type="link" onClick={switchFeedbackWindows}>
            Feedback
          </Button>
          &quot; section at the top right of the navigation bar.
        </>
      ),
      zh: (
        <>
          游戏《{wholeGameInfo.entity_name}
          》有一段时间没有被访问，因此已停止数据更新以节省资源。
          如果您需要查看最新数据，请联系{CONTACT_EMAIL}或在导航栏右上角的&quot;
          <Button type="link" onClick={switchFeedbackWindows}>
            反馈
          </Button>
          &quot;部分提交您的需求。
        </>
      ),
    },
  };
}
